import React, { FC } from 'react';
import * as yup from 'yup';
import { Flex, HStack, List, Stack, Text } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { passwordRegExp } from '@/libs/utils';
import { AuthStatus, useAuthDispatch, useAuthState } from '@/contexts/AuthContext';
import { Toaster, toaster } from '@/components/ui/toaster';
import { PasswordInput } from '@/components/ui/password-input';
import { Field } from '../ui/field';
import { PinInput } from '../ui/pin-input';
import { Button } from '../ui/button';

type ResetPasswordFormProps = {
  email: string;
};

const ResetPasswordForm: FC<ResetPasswordFormProps> = ({ email }) => {
  const { resetPassword, resetPasswordConfirm, dispatch } = useAuthDispatch();
  const { sessionStatus } = useAuthState();
  const { t } = useTranslation('auth');
  const onResetPasswordSchema = yup.object({
    code: yup
      .string()
      .matches(/^\d{6}$/, t('validation.codeInvalid'))
      .required(t('validation.codeRequired')),
    password: yup
      .string()
      .matches(passwordRegExp, {
        message: (cur) => {
          const fails: string[] = [];
          if (!cur.value.match(/[a-z]/)) {
            fails.push(t('validation.lowercase'));
          }
          if (!cur.value.match(/[A-Z]/)) {
            fails.push(t('validation.uppercase'));
          }
          if (!cur.value.match(/[0-9]/)) {
            fails.push(t('validation.numbers'));
          }
          if (!cur.value.match(/[!@#$%^&*]/)) {
            fails.push(t('validation.specialChars'));
          }
          if (cur.value.match(/^\s/)) {
            fails.push(t('validation.noSpaces'));
          }
          return t('validation.passwordRequirements', { fails: fails.join(', ') });
        }
      })
      .min(8, t('validation.passwordMinLength'))
      .required(t('validation.newPasswordRequired'))
  });
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(onResetPasswordSchema),
    defaultValues: {
      code: '',
      password: ''
    }
  });

  const onCancel = () => {
    dispatch({ type: 'clearSession' });
  };

  const onResendConfirmation = async () => {
    await resetPassword({ email });

    toaster.create({
      title: t('verify.resent'),
      description: t('verify.resentTo', { email }),
      type: 'success'
    });
  };

  const onSubmit = (evt: any) => {
    const { password, code } = evt;

    resetPasswordConfirm({ code, newPassword: password });
  };

  return (
    <>
      <Text textAlign='center' color='gray.500' mb={4} className='text-center grey-subtitle mb-4'>
        {t('reset.instructions')}
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={6}>
          {/* Code */}
          <Field
            required
            label={t('reset.verificationCode')}
            invalid={!!errors.code}
            errorText={errors.code?.message}
            helperText={t('reset.codeHint')}
          >
            <Controller
              name='code'
              control={control}
              render={({ field: { onChange } }) => (
                <HStack>
                  <PinInput
                    otp
                    autoFocus
                    count={6}
                    colorPalette='brand'
                    size='md'
                    // focusBorderColor='brand.300'
                    onValueComplete={(val) => onChange(val.valueAsString)}
                  />
                </HStack>
              )}
            />
          </Field>

          {/* Password */}
          <Field
            label={t('reset.newPassword')}
            required
            invalid={!!errors.password}
            errorText={errors.password?.message}
            helperText=''
          >
            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <PasswordInput
                  {...field}
                  required
                  id='password'
                  fontSize='md'
                  placeholder={t('reset.newPassword')}
                  pr='4.5rem'
                />
              )}
            />
            <Text fontSize='xs'>{t('reset.passwordRequirements')}</Text>
            <List.Root as='ol' fontSize='xs' px='4' pt='2'>
              <List.Item>{t('reset.reqMinLength')}</List.Item>
              <List.Item>{t('reset.reqNumber')}</List.Item>
              <List.Item>{t('reset.reqUppercase')}</List.Item>
              <List.Item>{t('reset.reqLowercase')}</List.Item>
              <List.Item>{t('reset.reqSpecial')}</List.Item>
            </List.Root>
          </Field>
        </Stack>

        <Flex align='center' justify='space-between' mt='8'>
          <Button variant='ghost' fontSize='sm' onClick={onCancel}>
            {t('reset.cancel')}
          </Button>
          <Button variant='ghost' fontSize='sm' onClick={onResendConfirmation}>
            {t('reset.didNotReceive')}
          </Button>
        </Flex>

        <Button
          type='submit'
          mt='8'
          w='full'
          loading={sessionStatus === AuthStatus.PENDING_RESET_PASSWORD_CONFIRM}
          disabled={!!errors.code || !!errors.password}
        >
          {t('reset.submit')}
        </Button>
      </form>
      <Toaster />
    </>
  );
};

export default ResetPasswordForm;
